import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import classNames from 'classnames';
import { Alert, Col, FormGroup } from 'reactstrap';
import Select from 'react-select';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Form, Formik } from 'formik';
import get from 'lodash/get';
import Cookies from 'js-cookie';
import AddSearchAlertModal from '../../../settings/searchalerts/modals/AddSearchAlertModal';
import {
  clearBidErrorMessage,
  fetchSearchAuctionFilters,
  placeBid,
  searchAuctions,
  setSearchQuery,
} from '../../../search/SearchActions';
import { AuctionSortType, AuctionSortTypeName } from '../../../constants/AuctionSortType';
import { AUCTION_PUBLISHED, SSE_BUYER, SSE_SELLER, SSEClient } from '../../../api/SSEClient';
import SearchBidModal from '../../../search/bidModals/SearchBidModal';
import {
  getAuction,
  handleAuctionEvent,
  refreshAuctionList,
  searchMyAuctions,
} from '../../../myvehicles/MyVehiclesActions';
import { searchMyBids } from '../../../mybids/MyBidsActions';
import { getAuctionsAggregatedByMake } from '../../../homepage/HomePageActions';
import { ListingType } from '../../../constants/ListingType';
import { closeAlert, showAlert } from '../alert/AlertActions';
import { replaceRouteParams } from '../../helpers/replaceRouteParams';
import AlertVariant from '../alert/AlertVariant';
import trashIcon from '../../../assets/img/icons/trash.svg';
import { REFRESH_AUCTION_SUCCESS } from '../../../search/SearchReducer';
import { MY_VEHICLES_REFRESH_SUCCESS } from '../../../myvehicles/MyVehiclesReducer';
import { MY_BIDS_REFRESH_SUCCESS } from '../../../mybids/MyBidsReducer';
import RouterPaths from '../../../constants/RouterPaths';
import notificationActiveIcon from '../../../assets/img/icons/notifications_active.svg';
import filtersIcon from '../../../assets/img/icons/filters.svg';
import closeWhiteIcon from '../../../assets/img/icons/close-white.svg';
import closeBlackIcon from '../../../assets/img/icons/close-black.svg';
import closeBlackSmallIcon from '../../../assets/img/icons/close-black-small.svg';
import leftArrowIcon from '../../../assets/img/icons/arrow-left-black.svg';
import rightArrowIcon from '../../../assets/img/icons/arrow-right-black.svg';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import CommonButton from '../CommonButton';
import { CommonButtonVariants } from '../../../constants/CommonButtonVariants';
import listViewIcon from '../../../assets/img/icons/list_view.svg';
import gridViewIcon from '../../../assets/img/icons/grid_view.svg';
import tableViewIcon from '../../../assets/img/icons/table_view.svg';
import redTrashIcon from '../../../assets/img/icons/trash-red.svg';
import cancelIcon from '../../../assets/img/icons/cancel.svg';
import FormSelectField from '../formFields/FormSelectField';
import { MileageName } from '../../../constants/MileageName';
import { constantToSelect } from '../../helpers/constantToSelect';
import { dataToSelect } from '../../helpers/dataToSelect';
import {
  fetchOrganizationLocations,
  getFavouriteAuctions,
} from '../../../settings/SettingsActions';
import {
  getVehicleMakeList,
  getVehicleModelList,
} from '../../../addvehicle/AddVehicleActions';
import { FormGroupRadioField } from '../formFields';
import { AuctionStatusName } from '../../../constants/AuctionStatus';
import { BidStatusType, BidStatusTypeName } from '../../../constants/BidStatusType';
import { generateYearOptions } from '../../helpers/generateYearOptions';
import { bodyTypes } from '../../../constants/bodyTypes';
import AuctionFilterCollapsible from './AuctionFilterCollapsible';
import CommonTable from '../CommonTable';
import { capitalizeString } from '../../helpers/capitalizeString';
import { formatDate } from './AuctionCountdown';
import { preparePriceFormat } from '../../helpers/preparePriceFormat';
import FormInputField from '../formFields/FormInputField';
import searchIcon from '../../../assets/img/icons/search.svg';
import {
  LOCATION_ALL_COOKIE_VALUE,
  LOCATION_ID_COOKIE,
  setLocationIdCookie,
} from '../../../constants/LocationCookie';
import useDefaultBidLocationId from '../../hooks/useDefaultBidLocationId';

const sseClientSeller = new SSEClient(SSE_SELLER);
const sseClientBuyer = new SSEClient(SSE_BUYER);

export const AuctionListTemplateContextType = {
  SEARCH: 'SEARCH',
  SELLING: 'SELLING',
  BUYING: 'BUYING',
  FAVOURITE_OFFERS: 'FAVOURITE_OFFERS',
};

const AuctionListTemplateTitle = {
  SEARCH: 'Results',
  SELLING: 'Selling',
  BUYING: 'Buying',
  FAVOURITE_OFFERS: 'Favourite Offers',
};

export const ViewType = {
  LIST: 'LIST',
  GRID: 'GRID',
  TABLE: 'TABLE',
};

function AuctionTableRow({ item }) {
  const registrationPlate = get(item, 'vehicle.registrationPlate')?.trim() || '-';
  const vehicleMake = get(item, 'vehicle.vehicleModel.makeName');
  const vehicleModel = get(item, 'vehicle.vehicleModel.name');
  const endsOn = get(item, 'endsOn');
  const autoAcceptPriceInPence = get(item, 'autoAcceptPriceInPence');
  const buyNowPriceInPence = get(item, 'buyNowPriceInPence');
  const highestBidInPence = get(item, 'highestBidInPence');
  const proxyBidInPence = get(item, 'proxyBidInPence');

  const formatPrice = (pence) => (pence ? preparePriceFormat(pence, true) : '-');
  const vehicleMakeModel =
    [vehicleMake, vehicleModel].filter(Boolean).map(capitalizeString).join(' ') || '-';
  const endsOnDisplay = endsOn ? formatDate(endsOn) : '-';

  return (
    <>
      <td>{registrationPlate}</td>
      <td>{vehicleMakeModel}</td>
      <td>{formatPrice(autoAcceptPriceInPence)}</td>
      <td>{formatPrice(proxyBidInPence || highestBidInPence)}</td>
      <td>{formatPrice(buyNowPriceInPence)}</td>
      <td>{endsOnDisplay}</td>
    </>
  );
}

const bodyTypeOptions = dataToSelect(
  bodyTypes.map((value) => ({ value, label: value })),
  ['value', 'label'],
);

const ALL_SELECTED = { value: null, label: 'All' };
const ANY_SELECTED = { value: null, label: 'Any' };

const normalizeEnum = (v) =>
  typeof v === 'string'
    ? v
        .trim()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toUpperCase()
    : v;

const mapCarTypeCriteria = (opt) => {
  if (!opt) return undefined;
  if (opt.value === null || opt.value === undefined) return null;
  const val = opt.value;
  return typeof val === 'string' ? val.toUpperCase() : val;
};

const sortByNameAsc = (arr) =>
  [...arr].sort((a, b) =>
    String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, {
      sensitivity: 'base',
      numeric: true,
    }),
  );

// shallow equality to avoid unnecessary state sets on unchanged aggs
const sameAgg = (a, b) =>
  a.length === b.length &&
  a.every((x, i) => {
    const y = b[i];
    return x.name === y.name && x.count === y.count && x.value === y.value;
  });

let formContext = null;

function SearchForm({ form, makeOptions, searchAuctions, onClearAll }) {
  const dispatch = useDispatch();
  const [modelOptions, setModelOptions] = useState([ALL_SELECTED]);

  const baseYearOptions = generateYearOptions(1950, new Date().getFullYear());

  const vehicleYearMinValueOptions = useMemo(
    () => [ANY_SELECTED, ...baseYearOptions],
    [baseYearOptions],
  );

  const vehicleYearMaxValueOptions = useMemo(() => {
    const minVal = form?.values?.vehicleYearMinValue?.value;
    if (minVal === undefined || minVal === null) {
      return [ANY_SELECTED, ...baseYearOptions];
    }
    const filtered = baseYearOptions.filter((opt) => opt.value >= minVal);
    return [ANY_SELECTED, ...filtered];
  }, [form?.values?.vehicleYearMinValue?.value, baseYearOptions]);

  useEffect(() => {
    const { make } = form.values;
    if (make?.value != null) {
      dispatch(
        getVehicleModelList(make.value, (models) => {
          setModelOptions([ALL_SELECTED, ...dataToSelect(models.data, ['id', 'name'])]);
        }),
      );
    } else {
      form.setFieldValue('model', ALL_SELECTED);
      setModelOptions([ALL_SELECTED]);
    }
  }, [dispatch, form?.values]);

  return (
    <Form>
      <FormSelectField
        name="make"
        label="Make"
        options={[ALL_SELECTED, ...makeOptions]}
        labelVariant="bordered"
        selectProps={{ menuPosition: 'fixed' }}
      />
      <FormSelectField
        name="model"
        label="Model"
        options={modelOptions}
        labelVariant="bordered"
        loadRightAway={false}
        disabled={form?.values?.make?.value == null}
        selectProps={{ menuPosition: 'fixed' }}
      />
      <FormSelectField
        name="carType"
        label="Body type"
        options={[ALL_SELECTED, ...bodyTypeOptions]}
        labelVariant="bordered"
        loadRightAway={false}
        selectProps={{ menuPosition: 'fixed' }}
      />
      <FormSelectField
        name="vehicleYearMinValue"
        label="Year FROM"
        options={vehicleYearMinValueOptions}
        labelVariant="bordered"
        loadRightAway={false}
        selectProps={{ menuPosition: 'fixed' }}
      />
      <FormSelectField
        name="vehicleYearMaxValue"
        label="Year TO"
        options={vehicleYearMaxValueOptions}
        labelVariant="bordered"
        loadRightAway={false}
        selectProps={{ menuPosition: 'fixed' }}
      />
      <button
        type="button"
        onClick={() => (typeof onClearAll === 'function' ? onClearAll() : form.resetForm())}
        className="d-block m-0 h-100 align-self-center clear-all-filters-button"
      >
        <img src={cancelIcon} alt="Cancel icon" />
        <span>Clear all filters</span>
      </button>
    </Form>
  );
}

function FiltersForm({
  form,
  context,
  searchAuctions,
  makeOptions,
  locationOptions,
  hasSingleLocation,
  setIsFiltersVisible,
  fuelTypes,
  setFuelTypes,
  transmissionTypes,
  setTransmissionTypes,
  colors,
  setColors,
  activeTab,
  onCriteriaChange,
}) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1424px)');
  const isDesktop = useMediaQuery('(min-width: 1425px)');

  useEffect(() => {
    if (!hasSingleLocation) return;
    const onlyRealOption = locationOptions.find((opt) => opt.value);
    if (onlyRealOption && form?.values?.location?.value !== onlyRealOption.value) {
      form.setFieldValue('location', onlyRealOption);
    }
  }, [hasSingleLocation, locationOptions, form?.values?.location?.value]);

  const mileageOptions = useMemo(() => [ANY_SELECTED, ...constantToSelect(MileageName)], []);

  const sellingAuctionStatusNames = useMemo(() => constantToSelect(AuctionStatusName), []);
  const buyingAuctionStatusNames = useMemo(
    () =>
      constantToSelect(BidStatusTypeName).filter(
        (opt) => opt.value !== BidStatusType.TO_ACCEPT,
      ),
    [],
  );

  const sellingAuctionStatusRadios = useMemo(
    () => [
      {
        label: 'ALL',
        value: -1,
      },
      ...sellingAuctionStatusNames,
    ],
    [sellingAuctionStatusNames],
  );
  const buyingAuctionStatusRadios = useMemo(
    () => [
      {
        label: 'ALL',
        value: -1,
      },
      ...buyingAuctionStatusNames,
    ],
    [buyingAuctionStatusNames],
  );

  const [modelOptions, setModelOptions] = useState([]);
  const [sectionsOpen, setSectionsOpen] = useState({
    mileage: false,
    fuel: false,
    transmission: false,
    colour: false,
    offerStatus: true,
    make: true,
    model: true,
    bodyType: true,
    year: true,
    vrm: true,
  });

  const toggleSection = (key) => {
    if (isDesktop) {
      setSectionsOpen((s) => {
        const collapsibleKeys = ['fuel', 'transmission', 'colour', 'offerStatus', 'status'];
        const next = { ...s };
        collapsibleKeys.forEach((k) => {
          next[k] = false;
        });
        next[key] = !s[key];
        return next;
      });
    } else {
      setSectionsOpen((s) => ({ ...s, [key]: !s[key] }));
    }
  };

  // helpers: preserve checked & sort; return prev if unchanged
  const mergeAgg = (prev, agg) => {
    const map = new Map(agg.map(({ value, count }) => [value, count]));
    const names = Array.from(map.keys());
    const next = sortByNameAsc(
      names.map((name) => {
        const old = prev.find((p) => p.name === name);
        return { name, count: map.get(name) ?? 0, value: old?.value ?? false };
      }),
    );
    return sameAgg(prev, next) ? prev : next;
  };

  // signatures of selection (ONLY change when user toggles boxes)
  const selFuelSig = useMemo(
    () =>
      fuelTypes
        .filter((x) => x.value)
        .map((x) => x.name)
        .sort()
        .join('|'),
    [fuelTypes],
  );
  const selTransSig = useMemo(
    () =>
      transmissionTypes
        .filter((x) => x.value)
        .map((x) => x.name)
        .sort()
        .join('|'),
    [transmissionTypes],
  );
  const selColorSig = useMemo(
    () =>
      colors
        .filter((x) => x.value)
        .map((x) => x.name)
        .sort()
        .join('|'),
    [colors],
  );

  // derive selected arrays from signatures (stable, only when selection changes)
  const fuelSelected = useMemo(
    () => (selFuelSig ? selFuelSig.split('|').filter(Boolean).map(normalizeEnum) : []),
    [selFuelSig],
  );
  const transSelected = useMemo(
    () => (selTransSig ? selTransSig.split('|').filter(Boolean).map(normalizeEnum) : []),
    [selTransSig],
  );
  const colorSelected = useMemo(
    () => (selColorSig ? selColorSig.split('|').filter(Boolean).map(normalizeEnum) : []),
    [selColorSig],
  );

  const {
    make,
    model,
    carType,
    vehicleYearMinValue,
    vehicleYearMaxValue,
    yearMinValue,
    yearMaxValue,
    mileage,
    status,
    vrm,
    location,
  } = form.values || {};

  // criteria object memoized from only meaningful primitives + selection sigs
  const criteria = useMemo(() => {
    const statusVal = status === -1 ? undefined : status;
    const hasLocationFilter = [
      AuctionListTemplateContextType.FAVOURITE_OFFERS,
      AuctionListTemplateContextType.SELLING,
      AuctionListTemplateContextType.BUYING,
    ].includes(context);
    const resolvedLocationId =
      hasLocationFilter && !hasSingleLocation ? location?.value : undefined;
    return {
      auctionVariant: activeTab,
      makeId: make?.value,
      modelId: model?.value,
      vrm: vrm?.trim() || undefined,
      carType: mapCarTypeCriteria(carType),
      vehicleYearMinValue: vehicleYearMinValue?.value ?? yearMinValue?.value,
      vehicleYearMaxValue: vehicleYearMaxValue?.value ?? yearMaxValue?.value,
      mileage: +(mileage?.value ?? 0) || undefined,
      fuelType: fuelSelected.length ? fuelSelected : undefined,
      transmission: transSelected.length ? transSelected : undefined,
      colour: colorSelected.length ? colorSelected : undefined,
      ...(resolvedLocationId ? { organizationLocationId: resolvedLocationId } : {}),
      ...(context === AuctionListTemplateContextType.BUYING
        ? { bidsSearchFilter: statusVal }
        : { status: statusVal }),
    };
  }, [
    activeTab,
    make?.value,
    model?.value,
    carType?.value,
    vehicleYearMinValue?.value,
    vehicleYearMaxValue?.value,
    yearMinValue?.value,
    yearMaxValue?.value,
    mileage?.value,
    status,
    fuelSelected,
    transSelected,
    colorSelected,
    context,
    vrm,
    location?.value,
    hasSingleLocation,
  ]);

  useEffect(() => {
    if (make?.value != null) {
      dispatch(
        getVehicleModelList(make?.value, (models) => {
          setModelOptions([ALL_SELECTED, ...dataToSelect(models.data, ['id', 'name'])]);
        }),
      );
    } else {
      form.setFieldValue('model', ALL_SELECTED);
      setModelOptions([ALL_SELECTED]);
    }
  }, [dispatch, make?.value]);

  useEffect(() => {
    if (
      context === AuctionListTemplateContextType.SELLING ||
      context === AuctionListTemplateContextType.FAVOURITE_OFFERS
    )
      return;
    (async () => {
      try {
        const body = {
          criteria,
          page: {
            pageNumber: 0,
            pageSize: 100,
            sort: { orders: [{ direction: 'ASC', property: 'lastLaunchedOn' }] },
          },
        };
        const response = await dispatch(fetchSearchAuctionFilters(body));
        const prep = (type) => {
          const values = response?.data?.data?.find((i) => i.type === type)?.aggregation ?? [];
          return sortByNameAsc(
            values.map(({ value, count }) => ({ name: value, value: false, count })),
          );
        };
        setFuelTypes(prep('FUEL'));
        setTransmissionTypes(prep('TRANSMISSION'));
        setColors(prep('COLOUR'));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]); // intentionally once except context

  const lastCriteriaSigRef = useRef(null);
  useEffect(() => {
    if (typeof onCriteriaChange === 'function') {
      onCriteriaChange(criteria);
    }
    const sig = JSON.stringify(criteria || {});
    if (lastCriteriaSigRef.current === sig) return;
    lastCriteriaSigRef.current = sig;
    searchAuctions({
      ...criteria,
      carType: criteria.carType?.toUpperCase?.() || criteria.carType,
      pageNumber: 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria]);

  // debounced re-fetch of aggregations on criteria change
  const debouncedRefetchAggRef = useRef(
    _.debounce(async (crit) => {
      try {
        const body = {
          criteria: crit,
          page: {
            pageNumber: 0,
            pageSize: 100,
            sort: { orders: [{ direction: 'ASC', property: 'lastLaunchedOn' }] },
          },
        };
        const response = await dispatch(fetchSearchAuctionFilters(body));
        const getAgg = (type) =>
          response?.data?.data?.find((it) => it.type === type)?.aggregation ?? [];

        setFuelTypes((prev) => mergeAgg(prev, getAgg('FUEL')));
        setTransmissionTypes((prev) => mergeAgg(prev, getAgg('TRANSMISSION')));
        setColors((prev) => mergeAgg(prev, getAgg('COLOUR')));
      } catch (e) {
        console.error('Failed to refetch filter aggregations', e);
      }
    }, 300),
  );

  useEffect(() => {
    if (
      context === AuctionListTemplateContextType.SELLING ||
      context === AuctionListTemplateContextType.FAVOURITE_OFFERS
    )
      return;
    debouncedRefetchAggRef.current(criteria);
  }, [criteria, context]);

  useEffect(() => () => debouncedRefetchAggRef.current.cancel(), []);

  const baseYearOptions = useMemo(
    () => generateYearOptions(1950, new Date().getFullYear()),
    [],
  );

  const vehicleYearMinValueOptions = useMemo(
    () => [ANY_SELECTED, ...baseYearOptions],
    [baseYearOptions],
  );

  const vehicleYearMaxValueOptions = useMemo(() => {
    const minVal = form?.values?.vehicleYearMinValue?.value;
    if (minVal === undefined || minVal === null) {
      return [ANY_SELECTED, ...baseYearOptions];
    }
    const filtered = baseYearOptions.filter((opt) => opt.value >= minVal);
    return [ANY_SELECTED, ...filtered];
  }, [form?.values?.vehicleYearMinValue?.value, baseYearOptions]);

  // Toggle fuel/transmission/colour checkbox and trigger search + aggregation once
  function handleFilterChange(event, name, type) {
    const mapToggled = (list) =>
      list.map((item) =>
        item.name === name
          ? {
              ...item,
              value: event.target.checked,
            }
          : item,
      );

    // compute next lists synchronously so we can trigger search + aggregation exactly once
    let nextFuel = fuelTypes;
    let nextTrans = transmissionTypes;
    let nextColour = colors;

    switch (type) {
      case 'FUEL':
        nextFuel = mapToggled(fuelTypes);
        setFuelTypes(nextFuel);
        break;
      case 'COLOUR':
        nextColour = mapToggled(colors);
        setColors(nextColour);
        break;
      case 'TRANSMISSION':
        nextTrans = mapToggled(transmissionTypes);
        setTransmissionTypes(nextTrans);
        break;
      default:
        return null;
    }
    return null;
  }

  const renderFilters = () => (
    <div>
      {[
        AuctionListTemplateContextType.BUYING,
        AuctionListTemplateContextType.SELLING,
        AuctionListTemplateContextType.FAVOURITE_OFFERS,
      ].includes(context) ? (
        <div className="form-group pt-2 px-1">
          <FormSelectField
            name="location"
            label="Location"
            rowClassNames={classNames(
              'pt-2',
              context === AuctionListTemplateContextType.FAVOURITE_OFFERS ? 'pb-0' : 'pb-3',
            )}
            options={locationOptions}
            disabled={hasSingleLocation}
            onChangeHandler={(location) => {
              setLocationIdCookie(location?.value);
            }}
            labelVariant="bordered"
            selectProps={{ menuPosition: 'fixed' }}
          />
          {[
            AuctionListTemplateContextType.BUYING,
            AuctionListTemplateContextType.SELLING,
          ].includes(context) && (
            <FormInputField
              name="vrm"
              label="VRM"
              labelVariant="bordered"
              icon={searchIcon}
              upperCase={true}
              maxLength={50}
              iconClassName="input-right-icon"
            />
          )}
        </div>
      ) : null}
      <div className="form-group pt-2">
        <FormSelectField
          name="make"
          label="Make"
          options={[ALL_SELECTED, ...makeOptions]}
          labelVariant="bordered"
          selectProps={{ menuPosition: 'fixed' }}
        />
      </div>
      <div className="form-group pt-2">
        <FormSelectField
          name="model"
          label="Model"
          options={modelOptions}
          labelVariant="bordered"
          loadRightAway={false}
          disabled={form?.values?.make?.value == null}
          selectProps={{ menuPosition: 'fixed' }}
        />
      </div>
      <div className="d-flex justify-content-between gap-4 pt-2">
        <div className="flex-grow-1">
          <FormSelectField
            name="vehicleYearMinValue"
            label="Year FROM"
            options={vehicleYearMinValueOptions}
            labelVariant="bordered"
            loadRightAway={false}
            selectProps={{ menuPosition: 'fixed' }}
          />
        </div>
        <div className="flex-grow-1">
          <FormSelectField
            name="vehicleYearMaxValue"
            label="Year TO"
            options={vehicleYearMaxValueOptions}
            labelVariant="bordered"
            loadRightAway={false}
            selectProps={{ menuPosition: 'fixed' }}
          />
        </div>
      </div>
      {context === AuctionListTemplateContextType.SELLING && (
        <AuctionFilterCollapsible
          title="Offer status"
          open={sectionsOpen.status}
          className="pb-2"
          onToggle={() => toggleSection('status')}
        >
          <div className="flex-grow-1">
            <FormGroupRadioField
              name="status"
              label="role"
              radios={sellingAuctionStatusRadios}
              extraClassName="mb-2 single-radio"
            />
          </div>
        </AuctionFilterCollapsible>
      )}
      {context === AuctionListTemplateContextType.BUYING && (
        <AuctionFilterCollapsible
          title="Offer status"
          open={sectionsOpen.status}
          className="pb-2"
          onToggle={() => toggleSection('status')}
        >
          <div className="flex-grow-1">
            <FormGroupRadioField
              name="status"
              label="role"
              radios={buyingAuctionStatusRadios}
              extraClassName="mb-2 single-radio"
            />
          </div>
        </AuctionFilterCollapsible>
      )}
    </div>
  );

  return (
    <Form>
      {!isDesktop && context !== AuctionListTemplateContextType.SEARCH && (
        <AuctionFilterCollapsible title="Search filters" defaultOpen>
          <div className="pt-2">
            <SearchForm
              form={form}
              makeOptions={makeOptions}
              searchAuctions={searchAuctions}
              onClearAll={() => {
                form.resetForm();
                setFuelTypes((prev) => prev.map((i) => ({ ...i, value: false })));
                setTransmissionTypes((prev) => prev.map((i) => ({ ...i, value: false })));
                setColors((prev) => prev.map((i) => ({ ...i, value: false })));
              }}
            />
          </div>
        </AuctionFilterCollapsible>
      )}
      {context === AuctionListTemplateContextType.SEARCH && (
        <>
          <div className="form-group pt-2">
            <FormSelectField
              name="make"
              label="Make"
              options={[ALL_SELECTED, ...makeOptions]}
              labelVariant="bordered"
              selectProps={{ menuPosition: 'fixed' }}
            />
          </div>
          <div className="form-group pt-2">
            <FormSelectField
              name="model"
              label="Model"
              options={modelOptions}
              labelVariant="bordered"
              loadRightAway={false}
              disabled={form?.values?.make?.value == null}
              selectProps={{ menuPosition: 'fixed' }}
            />
          </div>
          <div className="form-group pt-2">
            <FormSelectField
              name="carType"
              label="Body Type"
              options={[ALL_SELECTED, ...bodyTypeOptions]}
              labelVariant="bordered"
              loadRightAway={false}
              selectProps={{ menuPosition: 'fixed' }}
            />
          </div>
          <div className="d-flex justify-content-between gap-4 pt-2">
            <div className="flex-grow-1">
              <FormSelectField
                name="vehicleYearMinValue"
                label="Year FROM"
                options={vehicleYearMinValueOptions}
                labelVariant="bordered"
                loadRightAway={false}
                selectProps={{ menuPosition: 'fixed' }}
              />
            </div>
            <div className="flex-grow-1">
              <FormSelectField
                name="vehicleYearMaxValue"
                label="Year TO"
                options={vehicleYearMaxValueOptions}
                labelVariant="bordered"
                loadRightAway={false}
                selectProps={{ menuPosition: 'fixed' }}
              />
            </div>
          </div>
          <div className="form-group pt-2">
            <FormSelectField
              name="mileage"
              label="Mileage"
              options={mileageOptions}
              labelVariant="bordered"
              loadRightAway={false}
              selectProps={{ menuPosition: 'fixed' }}
            />
          </div>

          <AuctionFilterCollapsible
            title="Fuel"
            open={sectionsOpen.fuel}
            className="pb-2"
            onToggle={() => toggleSection('fuel')}
          >
            {fuelTypes.map(({ name, value, count }) => (
              <div
                className="d-flex justify-content-between align-items-center mb-2"
                key={name}
              >
                <label className="custom-checkbox-container">
                  <input
                    className="hidden-checkbox"
                    type="checkbox"
                    id={`fuel-${name}`}
                    checked={value}
                    onChange={(e) => handleFilterChange(e, name, 'FUEL')}
                  />
                  <span className="checkbox-custom-icon" />
                  <span className="checkbox-label-text">{name}</span>
                </label>
                <span className="search-page__filter-counter">{count}</span>
              </div>
            ))}
          </AuctionFilterCollapsible>

          <AuctionFilterCollapsible
            title="Transmission"
            open={sectionsOpen.transmission}
            className="pb-2"
            onToggle={() => toggleSection('transmission')}
          >
            {transmissionTypes.map(({ name, value, count }) => (
              <div
                className="d-flex justify-content-between align-items-center mb-2"
                key={name}
              >
                <label className="custom-checkbox-container">
                  <input
                    className="hidden-checkbox"
                    type="checkbox"
                    id={`transmission-${name}`}
                    checked={value}
                    onChange={(e) => handleFilterChange(e, name, 'TRANSMISSION')}
                  />
                  <span className="checkbox-custom-icon" />
                  <span className="checkbox-label-text">{name}</span>
                </label>
                <span className="search-page__filter-counter">{count}</span>
              </div>
            ))}
          </AuctionFilterCollapsible>

          <AuctionFilterCollapsible
            title="Colour"
            className="pb-2"
            open={sectionsOpen.colour}
            onToggle={() => toggleSection('colour')}
          >
            {colors.map(({ name, value, count }) => (
              <div
                className="d-flex justify-content-between align-items-center mb-2"
                key={name}
              >
                <label className="custom-checkbox-container">
                  <input
                    className="hidden-checkbox"
                    type="checkbox"
                    id={`colour-${name}`}
                    checked={value}
                    onChange={(e) => handleFilterChange(e, name, 'COLOUR')}
                  />
                  <span className="checkbox-custom-icon" />
                  <span className="checkbox-label-text">{name}</span>
                </label>
                <span className="search-page__filter-counter">{count}</span>
              </div>
            ))}
          </AuctionFilterCollapsible>
        </>
      )}
      {context !== AuctionListTemplateContextType.SEARCH && <>{renderFilters()}</>}
      {isMobile && (
        <div>
          <CommonButton
            handleClick={() => setIsFiltersVisible(false)}
            variant={CommonButtonVariants.PRIMARY}
            label="Filter"
            className={classNames('w-100 mb-3')}
          />
          <div className="d-flex gap-2">
            <CommonButton
              handleClick={() => {
                form.resetForm();
                setFuelTypes((prev) => prev.map((i) => ({ ...i, value: false })));
                setTransmissionTypes((prev) => prev.map((i) => ({ ...i, value: false })));
                setColors((prev) => prev.map((i) => ({ ...i, value: false })));
                setIsFiltersVisible(false);
              }}
              variant={CommonButtonVariants.OUTLINED_RED}
              label="Clear All"
              icon={redTrashIcon}
              iconAlt="Trash icon"
              iconLeft
              className={classNames('w-50')}
            />
            <CommonButton
              handleClick={() => setIsFiltersVisible(false)}
              variant={CommonButtonVariants.OUTLINED}
              label="Cancel"
              className={classNames('w-50')}
            />
          </div>
        </div>
      )}
      {isTablet && (
        <div>
          <div className="d-flex gap-2">
            <CommonButton
              handleClick={() => setIsFiltersVisible(false)}
              variant={CommonButtonVariants.PRIMARY}
              label="Filter"
              className={classNames('w-50')}
            />
            <CommonButton
              handleClick={() => setIsFiltersVisible(false)}
              variant={CommonButtonVariants.OUTLINED}
              label="Cancel"
              className={classNames('w-50')}
            />
          </div>
        </div>
      )}
    </Form>
  );
}

function AuctionListTemplate({
  context,
  searchItems,
  children,
  searchAuctionList,
  onLoadMore,
  onCriteriaChange,
  onSortChange,
  hasMore: hasMoreOverride,
  onItemRefresh,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const itemsRef = useRef({});
  const headerRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [refresh, setRefresh] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState(AuctionSortType.ENDING_SOONEST);
  const [status, setStatus] = useState(null);
  const [auctions, setAuctions] = useState([]);
  const [showBidModal, setShowBidModal] = useState(false);
  const [currentBidAuction, setCurrentBidAuction] = useState(null);

  const [scrollTo, setScrollTo] = useState(null);
  const stateHasMore = useSelector((state) => {
    if (context === AuctionListTemplateContextType.SELLING) {
      return state.myVehicles.get('hasMore');
    }
    if (context === AuctionListTemplateContextType.BUYING) {
      return state.myBids.get('hasMore');
    }
    return state.search.get('hasMore');
  });
  let hasMore = stateHasMore;
  if (typeof hasMoreOverride === 'boolean') {
    hasMore = hasMoreOverride;
  } else if (context === AuctionListTemplateContextType.FAVOURITE_OFFERS) {
    hasMore = false;
  }
  const refreshQuery = useSelector((state) => state.search.get('refreshQuery'));
  const makeId = useSelector((state) => state.search.get('makeId'));
  const searchQuery = useSelector((state) => state.search.get('searchQuery'));

  const searchTotalItems = useSelector((state) => state.search.get('totalItems'));
  const sellingTotalItems = useSelector((state) => state.myVehicles.get('totalItems'));
  const sellingItems = useSelector((state) => state.myVehicles.get('items')).toJS();

  const favouriteTotalItems = useSelector((state) => state.favourites.get('totalItems'));
  const buyingTotalItems = useSelector((state) => state.myBids.get('totalItems'));

  const organizationId = useSelector((state) => state.auth.get('organizationId'));

  const organizationLocations = useSelector((state) =>
    state.myOrganization.get('organizationLocations'),
  );

  const locationOptions = useMemo(
    () => [
      ALL_SELECTED,
      ...(Array.isArray(organizationLocations) ? organizationLocations : []).map((loc) => ({
        value: loc.id,
        label: loc.name || loc.address || loc?.postalCode?.postcode,
        isMain: loc.isMain,
      })),
    ],
    [organizationLocations],
  );

  const hasSingleLocation =
    (Array.isArray(organizationLocations) ? organizationLocations.length : 0) <= 1;

  useEffect(() => {
    dispatch(fetchOrganizationLocations());
  }, [dispatch]);

  const locationRestoredRef = useRef(false);

  useEffect(() => {
    const hasRealLocations =
      Array.isArray(organizationLocations) && organizationLocations.length > 0;
    if (!hasRealLocations || !formContext) return;

    const onlyRealOption = locationOptions.find((opt) => opt.value);
    if (hasSingleLocation && onlyRealOption) {
      if (formContext.values?.location?.value !== onlyRealOption.value) {
        formContext.setFieldValue('location', onlyRealOption);
      }
      return;
    }

    if (locationRestoredRef.current) return;
    locationRestoredRef.current = true;
    const rawCookie = Cookies.get(LOCATION_ID_COOKIE);
    const mainOption = locationOptions.find((opt) => opt.isMain) || locationOptions[0];
    let selectedOption;
    if (rawCookie === LOCATION_ALL_COOKIE_VALUE) {
      selectedOption = ALL_SELECTED;
    } else {
      selectedOption =
        locationOptions.find((opt) => opt.value && opt.value === rawCookie) || mainOption;
    }
    formContext.setFieldValue('location', selectedOption);
  }, [locationOptions, organizationLocations, hasSingleLocation]);

  const [activeTab, setActiveTab] = useState('ALL');

  const location = useLocation();

  const isDashboardTabsVisible = useMemo(
    () =>
      [
        RouterPaths.DASHBOARD,
        RouterPaths.MY_VEHICLES,
        RouterPaths.MY_BIDS,
        RouterPaths.ANALYTICS,
        RouterPaths.FAVOURITE_OFFERS,
      ].includes(location.pathname),
    [location.pathname],
  );

  const updateSearchItems = (newItem) => {
    // dispatch to the correct reducer based on context
    let type = REFRESH_AUCTION_SUCCESS;
    if (context === AuctionListTemplateContextType.SELLING) {
      type = MY_VEHICLES_REFRESH_SUCCESS;
    } else if (context === AuctionListTemplateContextType.BUYING) {
      type = MY_BIDS_REFRESH_SUCCESS;
    } else if (context === AuctionListTemplateContextType.SEARCH) {
      type = REFRESH_AUCTION_SUCCESS;
    }
    dispatch({
      type,
      payload: {
        item: newItem,
      },
    });
  };

  const clearAll = () => {
    scrollToHeader();
    setOffset(0);
    setScrollTo(null);
    setStatus(null);
    setSort(AuctionSortType.ENDING_SOONEST);

    navigate(location.pathname + location.search, { replace: true, state: {} });
  };

  useEffect(() => {
    dispatch(clearBidErrorMessage());
    dispatch(setSearchQuery('', true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    sseClientSeller.listen(
      auctions
        ?.filter((item) => item.organizationId === organizationId)
        .map((item) => item?.id),
      (response) => {
        dispatch(
          handleAuctionEvent(response, () => {
            if (response?.auctionId) {
              dispatch(
                getAuction(response.auctionId, (auction) => {
                  updateSearchItems(auction);
                }),
              );
            }
          }),
        );
      },
    );
    return () => {
      sseClientSeller.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctions, organizationId, dispatch]);

  useEffect(() => {
    const cleanup = () => {
      try {
        sseClientBuyer.close();
      } catch (e) {
        // already closed or not initialized
      }
    };

    // skip buyer SSE in Favourite Offers to avoid duplicate connections; the page manages it
    if (context === AuctionListTemplateContextType.FAVOURITE_OFFERS) {
      sseClientBuyer.close();
      return cleanup;
    }

    sseClientBuyer.listen(
      auctions.map((item) => item.id),
      (response) => {
        if (response?.type === AUCTION_PUBLISHED) {
          dispatch(
            refreshAuctionList(
              {
                auctionId: response?.auctionId,
                context: ListingType.SEARCH,
              },
              () =>
                dispatch(
                  showAlert(
                    'New vehicle available! Click here to check the details.',
                    AlertVariant.INFO,
                    null,
                    true,
                    () => {
                      try {
                        const auctionId = response?.auctionId;
                        if (auctionId) {
                          const detailsPath = replaceRouteParams(RouterPaths.SEARCH_DETAILS, {
                            id: auctionId,
                          });
                          navigate(detailsPath);
                        }
                      } finally {
                        dispatch(closeAlert());
                      }
                    },
                  ),
                ),
            ),
          );
        }
        dispatch(
          handleAuctionEvent(
            response,
            () => {
              if (!response?.auctionId) return;
              dispatch(
                getAuction(response.auctionId, (auction) => {
                  updateSearchItems(auction);
                }),
              );
            },
            dispatch(getAuctionsAggregatedByMake()),
          ),
        );
      },
    );

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctions, dispatch, context]);

  const lastCriteriaRef = useRef(null);

  const _searchAuctions = (values) => {
    if (loading) return;
    setLoading(true);

    // determine if this call represents a criteria change (not pagination/sort)
    const prevCriteria = lastCriteriaRef.current;
    const hasIncomingCriteria = values && typeof values === 'object';
    const isCriteriaChange = hasIncomingCriteria
      ? JSON.stringify(values) !== JSON.stringify(prevCriteria)
      : false;

    const baseValues = hasIncomingCriteria
      ? { ...values }
      : {
          search: searchQuery,
          makeId,
          offset: location?.state?.offset || offset,
          type: status,
        };

    if (isCriteriaChange) {
      baseValues.offset = 0;
      setOffset(0);
    }

    if (baseValues.offset == null) {
      baseValues.offset = location?.state?.offset || offset;
    }

    // attach current sort; SearchActions ignores it, MyVehiclesActions uses it
    if (baseValues && typeof baseValues === 'object' && !('sort' in baseValues)) {
      baseValues.sort = sort;
    }

    // update last criteria reference after computing isCriteriaChange
    if (hasIncomingCriteria) {
      lastCriteriaRef.current = { ...values };
    }

    if (context === AuctionListTemplateContextType.SELLING) {
      const offerStatus = location?.state?.status;
      if (offerStatus) {
        formContext.setFieldValue('status', offerStatus);
        navigate(location.pathname, { replace: true });
      }

      // for selling, hit my-auctions endpoint and do not touch local auctions list
      dispatch(
        searchMyAuctions({ ...baseValues, status: offerStatus || baseValues?.status }, () => {
          setLoading(false);
          setLoaded(true);
          scrollToItem();
        }),
      );
    } else if (context === AuctionListTemplateContextType.BUYING) {
      dispatch(
        searchMyBids(baseValues, (auctionsIds) => {
          setLoading(false);
          setAuctions((auctionsIds || []).map((id) => ({ id })));
          setLoaded(true);
          scrollToItem();
        }),
      );
    } else if (context === AuctionListTemplateContextType.FAVOURITE_OFFERS) {
      try {
        if (typeof searchAuctionList === 'function') {
          searchAuctionList(baseValues);
        }
      } finally {
        setLoading(false);
        setLoaded(true);
        scrollToItem();
      }
    } else {
      dispatch(
        searchAuctions(
          baseValues,
          sort,
          (auctions) => {
            setLoading(false);
            setAuctions(auctions);
            setLoaded(true);
            scrollToItem();
          },
          () => {
            setLoading(false);
          },
        ),
      );
    }
  };

  useEffect(() => {
    if (!_.has(location, 'state.offset')) {
      clearAll();
    }
  }, []);

  useEffect(() => {
    dispatch(getAuctionsAggregatedByMake());
    setRefresh(refreshQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, refreshQuery]);

  useEffect(() => {
    if (
      _.has(location, 'state.offset') &&
      _.has(location, 'state.auctionId') &&
      !_.isEmpty(searchItems)
    ) {
      const locationOffset = _.get(location, 'state.offset', 0);

      if (locationOffset !== 0) {
        setOffset(_.get(location, 'state.offset', 0) + 1);
      }

      setStatus(_.get(location, 'state.status', null));
      setScrollTo(location.state.auctionId);

      navigate(location.pathname + location.search, { replace: true, state: {} });
      return;
    }

    if (context === AuctionListTemplateContextType.SEARCH) {
      if (searchAuctionList) searchAuctionList({ sort });
      if (lastCriteriaRef.current) {
        _searchAuctions({ ...lastCriteriaRef.current });
      } else {
        _searchAuctions();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, offset, sort, dispatch, status, refresh, context]);

  // BUYING: Trigger fetch on offset/sort changes using last known criteria from FiltersForm
  useEffect(() => {
    if (context !== AuctionListTemplateContextType.BUYING) return;
    if (!lastCriteriaRef.current) return;
    _searchAuctions({ ...lastCriteriaRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, sort, context]);

  // SELLING: Trigger fetch on offset/sort changes using last known criteria from FiltersForm
  useEffect(() => {
    if (context !== AuctionListTemplateContextType.SELLING) return;
    if (!lastCriteriaRef.current) return;
    _searchAuctions({ ...lastCriteriaRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, sort, context]);

  const [favouriteItems, setFavouriteItems] = useState([]);
  const favouriteLocationId = useDefaultBidLocationId();

  useEffect(() => {
    if (
      context === AuctionListTemplateContextType.SELLING ||
      context === AuctionListTemplateContextType.FAVOURITE_OFFERS
    )
      return;
    if (!favouriteLocationId) return;
    (async () => {
      try {
        const body = {
          criteria: { organizationLocationId: favouriteLocationId },
          page: {
            pageNumber: 0,
            pageSize: 100,
          },
        };
        const response = await dispatch(getFavouriteAuctions(body));
        setFavouriteItems(response?.data?.data?.content);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    })();
  }, [context, favouriteLocationId]);

  const scrollToItem = () => {
    if (!scrollTo) return;
    if (itemsRef.current[scrollTo]) {
      itemsRef.current[scrollTo].scrollIntoView({ behavior: 'auto', block: 'center' });
      setScrollTo(null);
    }
  };

  const scrollToHeader = () => {
    if (!headerRef || !headerRef.current) return;
    headerRef.current.scrollIntoView({ behavior: 'auto', block: 'center' });
  };

  const loadData = () => {
    if (
      context === AuctionListTemplateContextType.FAVOURITE_OFFERS &&
      typeof onLoadMore === 'function'
    ) {
      onLoadMore();
      return;
    }
    // For SEARCH, BUYING, and now SELLING contexts, we manage pagination via local offset
    setOffset((prev) => prev + 1);
  };

  const handleChangeSort = (option) => {
    const newSort = option?.value || AuctionSortType.ENDING_SOONEST;
    setSort(newSort);
    setOffset(0);
    if (
      context === AuctionListTemplateContextType.SELLING ||
      context === AuctionListTemplateContextType.FAVOURITE_OFFERS ||
      context === AuctionListTemplateContextType.BUYING
    ) {
      if (typeof onSortChange === 'function') {
        onSortChange(newSort);
      }
    }
  };

  const toggleBidModal = () => {
    setShowBidModal(false);
  };

  const handlePlaceBid = (amount) => {
    const mainLocationOption =
      locationOptions.find((opt) => opt.isMain) || locationOptions.find((opt) => opt.value);
    const organizationLocationId =
      formContext?.values?.location?.value || mainLocationOption?.value || null;
    dispatch(placeBid(currentBidAuction?.id, amount * 100, null, organizationLocationId));
  };

  const [makeOptions, setMakeOptions] = useState([]);

  useEffect(() => {
    dispatch(
      getVehicleMakeList((makeList) => {
        setMakeOptions(dataToSelect(makeList?.data, ['id', 'name']));
      }),
    );
  }, [dispatch]);

  const [viewType, setViewType] = useState(ViewType.LIST);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const isAuthenticated = useSelector((state) => state.auth.get('isAuthenticated'));
  const [showAddAlertModal, setShowAddAlertModal] = useState(false);
  const [searchAlertInitialValues, setSearchAlertInitialValues] = useState(undefined);

  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1424px)');
  const isMobileOrTablet = useMediaQuery('(max-width: 1424px)');
  const isDesktop = useMediaQuery('(min-width: 1425px)');

  const filtersSuffix = (isPlural) => {
    switch (context) {
      case AuctionListTemplateContextType.SELLING:
        return `vehicle${isPlural ? 's' : ''} selling`;
      case AuctionListTemplateContextType.BUYING:
        return `vehicle${isPlural ? 's' : ''} buying`;
      case AuctionListTemplateContextType.FAVOURITE_OFFERS:
        return `favourite offer${isPlural ? 's' : ''}`;
      default:
        return '';
    }
  };

  const offerWord = (n) => (Number(n) === 1 ? 'offer' : 'offers');

  useEffect(() => {
    if (isMobile && viewType === ViewType.LIST) {
      setViewType(ViewType.GRID);
    }
  }, [isMobile, viewType]);

  const TABS = ['ALL', 'AUCTION', 'BUY_NOW'];

  const renderFilterTabs = (form) => {
    const headerNode = (() => {
      if (context === AuctionListTemplateContextType.SEARCH) {
        return (
          <div className="search-page__filter-box-search">
            <span />
            {!isDesktop && (
              <button type="button" onClick={() => setIsFiltersVisible(false)}>
                <img src={closeBlackIcon} alt="Close icon" />
              </button>
            )}
          </div>
        );
      }

      const totalsByContext = {
        [AuctionListTemplateContextType.SELLING]: sellingTotalItems,
        [AuctionListTemplateContextType.BUYING]: buyingTotalItems,
        [AuctionListTemplateContextType.FAVOURITE_OFFERS]: favouriteTotalItems,
      };

      const total = totalsByContext[context];
      if (typeof total === 'undefined') return null;

      return (
        <div className="search-page__filter-box">
          <span />
          <span>
            {total} {filtersSuffix(searchItems?.length > 1 || searchItems?.length === 0)}
          </span>
          {!isDesktop && (
            <button type="button" onClick={() => setIsFiltersVisible(false)}>
              <img src={closeWhiteIcon} alt="Close icon" />
            </button>
          )}
        </div>
      );
    })();

    const timerRef = useRef(null);

    const searchAuctionsDebounced = useCallback(
      (values) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          _searchAuctions(values);
        }, 300);
      },
      [_searchAuctions],
    );

    useEffect(() => () => clearTimeout(timerRef.current), []);

    return (
      <div className="container-fluid min-h-screen bg-gray-100 flex justify-center items-start p-0 filter-tabs">
        <div className="flex bg-gray-200 rounded-lg overflow-hidden mb-4 search-page__sort">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={classNames('search-page__sort-button', {
                'search-page__sort-button--active': activeTab === tab,
              })}
              onClick={() => {
                setActiveTab(tab);
                setOffset(0);
              }}
            >
              {tab.replace('_', ' ')}
            </button>
          ))}
        </div>

        <div
          className={classNames(
            'bg-white rounded-xl w-full max-w-md search-page__filters-box',
            {
              'search-page__filters-box-search__search':
                context === AuctionListTemplateContextType.SEARCH,
              'search-page__filters-box--visible': isFiltersVisible && !isDesktop,
              'search-page__filters-box--invisible': !isFiltersVisible && !isDesktop,
            },
          )}
        >
          {headerNode}

          <FiltersForm
            form={form}
            makeOptions={makeOptions}
            locationOptions={locationOptions}
            hasSingleLocation={hasSingleLocation}
            searchAuctions={searchAuctionsDebounced}
            context={context}
            setIsFiltersVisible={setIsFiltersVisible}
            fuelTypes={fuelTypes}
            setFuelTypes={setFuelTypes}
            transmissionTypes={transmissionTypes}
            setTransmissionTypes={setTransmissionTypes}
            colors={colors}
            setColors={setColors}
            activeTab={activeTab}
            onCriteriaChange={onCriteriaChange}
          />
        </div>
      </div>
    );
  };

  const renderFiltersButton = () => {
    const count = filterChips()?.length || 0;
    return (
      <div className="position-relative">
        <button
          onClick={() => setIsFiltersVisible((prevState) => !prevState)}
          type="button"
          className="common-button primary-button"
        >
          <img src={filtersIcon} alt="Filters icon" />
          <span>Filters</span>
        </button>
        {count > 0 ? <span className="filters-count">{count}</span> : null}
      </div>
    );
  };

  const renderViewTypeButtons = () => (
    <div>
      <div className="view-type-btns d-flex justify-content-end column-gap-2">
        {!isMobile && (
          <>
            <CommonButton
              handleClick={() => setViewType(ViewType.LIST)}
              variant={
                viewType === ViewType.LIST
                  ? CommonButtonVariants.OUTLINED_RED
                  : CommonButtonVariants.OUTLINED_WHITE
              }
              label=""
              icon={listViewIcon}
              iconAlt="List view"
              className={classNames('view-type-btn')}
            />
            <CommonButton
              handleClick={() => setViewType(ViewType.GRID)}
              variant={
                viewType === ViewType.GRID
                  ? CommonButtonVariants.OUTLINED_RED
                  : CommonButtonVariants.OUTLINED_WHITE
              }
              label=""
              icon={gridViewIcon}
              iconAlt="Grid view"
              className={classNames('view-type-btn')}
            />
            {context === AuctionListTemplateContextType.SELLING && (
              <CommonButton
                handleClick={() => setViewType(ViewType.TABLE)}
                variant={
                  viewType === ViewType.TABLE
                    ? CommonButtonVariants.OUTLINED_RED
                    : CommonButtonVariants.OUTLINED_WHITE
                }
                label=""
                icon={tableViewIcon}
                iconAlt="Table view"
                className={classNames('view-type-btn')}
              />
            )}
          </>
        )}
        {context === AuctionListTemplateContextType.SEARCH &&
          (isMobile ? (
            <CommonButton
              handleClick={() => {
                if (!isAuthenticated) {
                  navigate(RouterPaths.LOGIN);
                  return;
                }
                const fv = formContext?.values || {};
                const makeOpt = fv.make;
                const modelOpt = fv.model;
                const yearFromOpt = fv.yearMaxValue || fv.vehicleYearMaxValue;
                const yearToOpt = fv.yearMinValue || fv.vehicleYearMinValue;
                const mileageOpt = fv.mileage;
                setSearchAlertInitialValues({
                  vehicleMakeId: makeOpt?.value ?? null,
                  vehicleMakeName: makeOpt?.label ?? null,
                  vehicleModelId: modelOpt?.value ?? null,
                  vehicleModelName: modelOpt?.label ?? null,
                  yearFrom: yearFromOpt?.value ?? -1,
                  yearTo: yearToOpt?.value ?? -1,
                  mileageMaxValue: mileageOpt?.value ?? -1,
                });
                setShowAddAlertModal(true);
              }}
              variant={CommonButtonVariants.OUTLINED_RED}
              label=""
              icon={notificationActiveIcon}
              iconAlt="Notification active icon"
              className="search-alert-btn"
            />
          ) : (
            <CommonButton
              handleClick={() => {
                if (!isAuthenticated) {
                  navigate(RouterPaths.LOGIN);
                  return;
                }
                const fv = formContext?.values || {};
                const makeOpt = fv.make;
                const modelOpt = fv.model;
                const yearFromOpt = fv.yearMaxValue || fv.vehicleYearMaxValue;
                const yearToOpt = fv.yearMinValue || fv.vehicleYearMinValue;
                const mileageOpt = fv.mileage;
                setSearchAlertInitialValues({
                  vehicleMakeId: makeOpt?.value ?? null,
                  vehicleMakeName: makeOpt?.label ?? null,
                  vehicleModelId: modelOpt?.value ?? null,
                  vehicleModelName: modelOpt?.label ?? null,
                  yearFrom: yearFromOpt?.value ?? -1,
                  yearTo: yearToOpt?.value ?? -1,
                  mileageMaxValue: mileageOpt?.value ?? -1,
                });
                setShowAddAlertModal(true);
              }}
              variant={CommonButtonVariants.OUTLINED_RED}
              label={
                <span className="d-flex align-items-center">
                  <img
                    src={notificationActiveIcon}
                    alt="Notification active icon"
                    className="me-2"
                  />
                  Search alert
                </span>
              }
            />
          ))}
      </div>
    </div>
  );

  const renderFavouritePropToChildren = (child) =>
    favouriteItems?.length
      ? favouriteItems?.find((item) => item.id === child?.props?.item?.id)
      : false;

  const isAllLocationsScope = () =>
    context === AuctionListTemplateContextType.FAVOURITE_OFFERS &&
    !hasSingleLocation &&
    !formContext?.values?.location?.value;

  const showAuctionDetails = (id) => {
    const url = replaceRouteParams(RouterPaths.SEARCH_DETAILS, {
      id,
    });
    window.open(url, '_blank');
  };

  const renderChildren = () => {
    if (viewType === ViewType.TABLE) {
      return (
        <Col xs={`12 ${filterChips()?.length ? 'mt-4' : 'mt-0'}`}>
          {sellingTotalItems ? (
            <CommonTable
              className="auctions-table"
              headerOptions={[
                'VRM number',
                'Make Model',
                'Reserve price',
                'Current bid',
                'Buy Now price',
                'Ends time',
              ]}
              rowComponent={AuctionTableRow}
              onRowClick={showAuctionDetails}
              hasRowPointer={true}
              data={{
                items: sellingItems,
                totalItems: sellingTotalItems,
              }}
              offset={offset}
              pagination={true}
              onOffsetChange={(page) => setOffset(page)}
            />
          ) : (
            <Alert color="info">Looks like there are no offers yet.</Alert>
          )}
        </Col>
      );
    }
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        if (child.type === React.Fragment) {
          return React.cloneElement(
            child,
            null,
            React.Children.map(child.props.children, (grandchild) => {
              if (React.isValidElement(grandchild)) {
                return React.cloneElement(grandchild, {
                  viewType,
                  isFavourite: renderFavouritePropToChildren(grandchild),
                  isAllLocationsScope: isAllLocationsScope(),
                });
              }
              return grandchild;
            }),
          );
        }

        return React.cloneElement(child, {
          viewType,
          isFavourite: renderFavouritePropToChildren(child),
          isAllLocationsScope: isAllLocationsScope(),
        });
      }
      return child;
    });
  };

  const [fuelTypes, setFuelTypes] = useState([]);
  const [transmissionTypes, setTransmissionTypes] = useState([]);
  const [colors, setColors] = useState([]);

  const chipsTrackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const appliedWrapperRef = useRef(null);
  const resetBtnBoxRef = useRef(null);
  const [appliedMaxWidth, setAppliedMaxWidth] = useState(null);

  const updateChipsScrollState = () => {
    const el = chipsTrackRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 1);
  };

  const scrollChipsBy = (delta) => {
    const el = chipsTrackRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, top: 0, behavior: 'smooth' });
    setTimeout(updateChipsScrollState, 80);
  };

  const recomputeAppliedWidth = useCallback(() => {
    const wrap = appliedWrapperRef.current;
    if (!wrap) return;
    const wrapWidth = wrap.clientWidth;
    const resetWidth = resetBtnBoxRef.current?.offsetWidth ?? 0;
    const GAP = 8;
    const next = Math.max(0, wrapWidth - resetWidth - GAP);
    if (appliedMaxWidth !== next) setAppliedMaxWidth(next);
  }, [appliedMaxWidth]);

  useLayoutEffect(() => {
    recomputeAppliedWidth();
    const ro = new ResizeObserver(recomputeAppliedWidth);
    if (appliedWrapperRef.current) ro.observe(appliedWrapperRef.current);
    if (resetBtnBoxRef.current) ro.observe(resetBtnBoxRef.current);
    window.addEventListener('resize', recomputeAppliedWidth);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recomputeAppliedWidth);
    };
  }, [recomputeAppliedWidth]);

  useEffect(() => {
    const id = requestAnimationFrame(recomputeAppliedWidth);
    return () => cancelAnimationFrame(id);
  }, [formContext?.values, fuelTypes, transmissionTypes, colors, recomputeAppliedWidth]);

  useEffect(() => {
    updateChipsScrollState();
    const onResize = () => updateChipsScrollState();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, isTablet]);

  useEffect(() => {
    // Recalculate when filters set changes
    setTimeout(updateChipsScrollState, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formContext?.values, fuelTypes, transmissionTypes, colors]);

  const filterChips = () => {
    const chips = [];

    // helper to apply search + aggregation after arrays change (avoids duplicate requests)
    const applyAfterToggle = (nextFuel, nextTrans, nextColour) => {
      // update selection state first
      setFuelTypes(nextFuel);
      setTransmissionTypes(nextTrans);
      setColors(nextColour);
    };

    const isAllAny = (val) => {
      if (!val || typeof val !== 'object') return false;
      const hasValue = Object.prototype.hasOwnProperty.call(val, 'value');
      const hasLabel = Object.prototype.hasOwnProperty.call(val, 'label');
      if (hasValue && (val.value === null || val.value === undefined)) return true;
      if (hasLabel) {
        const lbl = String(val.label || '')
          .trim()
          .toLowerCase();
        if (lbl === 'all' || lbl === 'any') return true;
      }
      return false;
    };

    const CHIPS_LABELS = Object.freeze({
      carType: 'BODY TYPE',
      vehicleYearMinValue: 'YEAR FROM',
      yearMinValue: 'YEAR FROM',
      vehicleYearMaxValue: 'YEAR TO',
      yearMaxValue: 'YEAR TO',
      status: 'STATUS',
    });

    const displayLabelFor = (chip) => CHIPS_LABELS[chip] ?? chip;

    Object.entries(formContext.values || {})
      .filter(([key, v]) => {
        if (key === 'status' && (v === -1 || v == null)) return false;
        if (
          key === 'location' &&
          (hasSingleLocation || context === AuctionListTemplateContextType.SEARCH)
        )
          return false;
        return Boolean(v) && !isAllAny(v);
      })
      .forEach(([key, v]) => {
        let valueLabel;
        if (key === 'status') {
          const code = typeof v === 'object' && v !== null && 'value' in v ? v.value : v;
          let disp = AuctionStatusName?.[code] || String(code);
          if (code === 'PUBLISHED') disp = 'LIVE';
          valueLabel = String(disp).toUpperCase().replace(/_/g, ' ');
        } else {
          valueLabel = typeof v === 'object' && 'label' in v ? v.label : String(v);
        }
        chips.push({
          key,
          propLabel: displayLabelFor(key),
          valueLabel,
          onRemove: () => {
            if (key === 'location') {
              formContext.setFieldValue(key, ALL_SELECTED);
            } else {
              formContext.setFieldValue(key, null);
            }
          },
        });
      });

    const addArrayChips = (typeLabel, list, typeKey) => {
      list
        .filter((i) => i.value)
        .forEach((i) => {
          chips.push({
            key: `${typeLabel}:${i.name}`,
            propLabel: typeLabel,
            valueLabel: i.name,
            onRemove: () => {
              if (typeKey === 'FUEL') {
                const nextFuel = list.map((it) => ({
                  ...it,
                  value: it.name === i.name ? false : it.value,
                }));
                applyAfterToggle(nextFuel, transmissionTypes, colors);
              } else if (typeKey === 'TRANSMISSION') {
                const nextTrans = list.map((it) => ({
                  ...it,
                  value: it.name === i.name ? false : it.value,
                }));
                applyAfterToggle(fuelTypes, nextTrans, colors);
              } else if (typeKey === 'COLOUR') {
                const nextColour = list.map((it) => ({
                  ...it,
                  value: it.name === i.name ? false : it.value,
                }));
                applyAfterToggle(fuelTypes, transmissionTypes, nextColour);
              }
            },
          });
        });
    };

    addArrayChips('FUEL', fuelTypes, 'FUEL');
    addArrayChips('TRANSMISSION', transmissionTypes, 'TRANSMISSION');
    addArrayChips('COLOUR', colors, 'COLOUR');

    return chips.map((c) => (
      <span className="applied-filters__chips" key={c.key}>
        <span className="applied-filters__chips-filter-property">{c.propLabel}:</span>
        <span className="applied-filters__chips-filter-value">{c.valueLabel}</span>
        <button
          type="button"
          className="applied-filters__chips-filter-btn-icon"
          onClick={() => {
            if (c.key === 'location') setLocationIdCookie(LOCATION_ALL_COOKIE_VALUE);
            c.onRemove();
          }}
        >
          <img src={closeBlackSmallIcon} alt="Close icon" />
        </button>
      </span>
    ));
  };

  return (
    <Formik
      initialValues={{
        make: ALL_SELECTED,
        model: ALL_SELECTED,
        carType: ALL_SELECTED,
        vehicleYearMinValue: ANY_SELECTED,
        vehicleYearMaxValue: ANY_SELECTED,
        mileage: ANY_SELECTED,
        location: ALL_SELECTED,
        status:
          context === AuctionListTemplateContextType.SELLING ||
          context === AuctionListTemplateContextType.BUYING
            ? -1
            : undefined,
      }}
      onSubmit={() => null}
    >
      {(form) => {
        formContext = form;
        return (
          <div className="search-page-outer">
            {context === AuctionListTemplateContextType.SEARCH && (
              <div className="offers-page-header">
                <div className="offers-page-header-title">
                  <h3>Offers</h3>
                </div>
              </div>
            )}
            <div
              className={`search-page ${isDashboardTabsVisible ? 'search-page--filters-bar-visible' : ''}`}
            >
              <div className="search-page__filters">
                <div className="d-flex justify-content-between">
                  <div className="d-flex align-items-center search-page__filters-results">
                    <h2>{AuctionListTemplateTitle?.[context]}</h2>
                    {context === AuctionListTemplateContextType.SEARCH && (
                      <span>
                        {searchTotalItems} {offerWord(searchTotalItems)}
                      </span>
                    )}
                    {isMobileOrTablet &&
                      context === AuctionListTemplateContextType.SELLING && (
                        <span>
                          {sellingTotalItems} {offerWord(sellingTotalItems)}
                        </span>
                      )}
                    {isMobileOrTablet && context === AuctionListTemplateContextType.BUYING && (
                      <span>
                        {buyingTotalItems} {offerWord(buyingTotalItems)}
                      </span>
                    )}
                    {isMobileOrTablet &&
                      context === AuctionListTemplateContextType.FAVOURITE_OFFERS && (
                        <span>
                          {favouriteTotalItems} {offerWord(favouriteTotalItems)}
                        </span>
                      )}
                  </div>

                  {!isDesktop && renderViewTypeButtons()}
                </div>
                {!isTablet && renderFilterTabs(form)}
              </div>
              <div className="search-page__content">
                <div className="search-page__filters-and-sort">
                  {!isDesktop && renderFiltersButton()}
                  {isTablet && renderFilterTabs(form)}
                  <div>
                    <FormGroup>
                      <Select
                        options={[
                          {
                            value: AuctionSortType.ENDING_SOONEST,
                            label: AuctionSortTypeName.ENDING_SOONEST,
                          },
                          {
                            value: AuctionSortType.LATEST_ADDED,
                            label: AuctionSortTypeName.LATEST_ADDED,
                          },
                        ]}
                        onChange={handleChangeSort}
                        placeholder="Sort by"
                        classNamePrefix="react-select"
                        defaultValue={{
                          value: AuctionSortType.ENDING_SOONEST,
                          label: AuctionSortTypeName.ENDING_SOONEST,
                        }}
                      />
                    </FormGroup>
                  </div>
                  {isDesktop && renderViewTypeButtons()}
                </div>

                {filterChips()?.length ? (
                  <div className="applied-filters-wrapper" ref={appliedWrapperRef}>
                    <div
                      className="applied-filters"
                      style={appliedMaxWidth ? { maxWidth: appliedMaxWidth } : undefined}
                    >
                      <span className="applied-filters__title">Applied filters:</span>
                      <div className="applied-filters__chips-scroll">
                        {canScrollLeft && (
                          <button
                            type="button"
                            aria-label="Scroll left"
                            className={classNames('applied-filters__nav-btn', {
                              'applied-filters__nav-btn--disabled': !canScrollLeft,
                            })}
                            onClick={() => scrollChipsBy(-240)}
                          >
                            <img src={leftArrowIcon} alt="Left" />
                          </button>
                        )}
                        <div className="applied-filters__chips-viewport">
                          <div
                            className="applied-filters__chips-track"
                            ref={chipsTrackRef}
                            onScroll={updateChipsScrollState}
                          >
                            {filterChips(form)}
                          </div>
                        </div>
                        {canScrollRight && (
                          <button
                            type="button"
                            aria-label="Scroll right"
                            className={classNames('applied-filters__nav-btn', {
                              'applied-filters__nav-btn--disabled': !canScrollRight,
                            })}
                            onClick={() => scrollChipsBy(240)}
                          >
                            <img src={rightArrowIcon} alt="Right" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div ref={resetBtnBoxRef}>
                      <CommonButton
                        handleClick={() => {
                          form.resetForm();
                          setFuelTypes((prev) => prev.map((i) => ({ ...i, value: false })));
                          setTransmissionTypes((prev) =>
                            prev.map((i) => ({ ...i, value: false })),
                          );
                          setColors((prev) => prev.map((i) => ({ ...i, value: false })));
                        }}
                        variant={CommonButtonVariants.RED}
                        label=""
                        icon={trashIcon}
                        iconAlt="Trash icon"
                        className={classNames('view-type-btn')}
                      />
                    </div>
                  </div>
                ) : null}
                <InfiniteScroll
                  next={loadData}
                  hasMore={hasMore}
                  dataLength={searchItems?.length}
                  className={
                    viewType === ViewType.GRID && searchItems?.length ? 'auction-grid' : ''
                  }
                  style={{ height: 'auto', overflow: 'clip' }}
                >
                  {renderChildren()}
                </InfiniteScroll>
                {showBidModal && (
                  <SearchBidModal
                    toggleShow={toggleBidModal}
                    item={currentBidAuction}
                    handlePlaceBid={handlePlaceBid}
                  />
                )}
                {showAddAlertModal && (
                  <AddSearchAlertModal
                    isOpen={showAddAlertModal}
                    toggle={() => setShowAddAlertModal(false)}
                    onSave={() => setShowAddAlertModal(false)}
                    initialValues={searchAlertInitialValues}
                  />
                )}
              </div>
            </div>
          </div>
        );
      }}
    </Formik>
  );
}

export default AuctionListTemplate;
